import { useEffect, useRef } from "react";
import { Box, Typography } from "@mui/material";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import shp from "shpjs";

// Fix Leaflet's default icons issue in React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjUiIGhlaWdodD0iNDEiIHZpZXdCb3g9IjAgMCAyNSA0MSIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTEyLjUgMEMxOS40MDM2IDAgMjUgNS41OTY0NCAyNSAxMi41QzI1IDE5LjQwMzYgMTkuNDAzNiAyNSAxMi41IDI1QzUuNTk2NDQgMjUgMCAxOS40MDM2IDAgMTIuNUMwIDUuNTk2NDQgNS41OTY0NCAwIDEyLjUgMFoiIGZpbGw9IiMzMzg4ZmYiLz4KPHBhdGggZD0iTTEyLjUgNDFMMTkuNSAyNUg1LjVMMTIuNSA0MVoiIGZpbGw9IiMzMzg4ZmYiLz4KPC9zdmc+",
  iconUrl:
    "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjUiIGhlaWdodD0iNDEiIHZpZXdCb3g9IjAgMCAyNSA0MSIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTEyLjUgMEMxOS40MDM2IDAgMjUgNS41OTY0NCAyNSAxMi41QzI1IDE5LjQwMzYgMTkuNDAzNiAyNSAxMi41IDI1QzUuNTk2NDQgMjUgMCAxOS40MDM2IDAgMTIuNUMwIDUuNTY0NCAwIDEyLjUgMFoiIGZpbGw9IiMzMzg4ZmYiLz4KPHBhdGggZD0iTTEyLjUgNDFMMTkuNSAyNUg1LjVMMTIuNSA0MVoiIGZpbGw9IiMzMzg4ZmYiLz4KPC9zdmc+",
  shadowUrl:
    "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDEiIGhlaWdodD0iNDEiIHZpZXdCb3g9IjAgMCA0MSA0MSIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGVsbGlwc2UgY3g9IjIwLjUiIGN5PSIzNy41IiByeD0iMjAuNSIgcnk9IjMuNSIgZmlsbD0iYmxhY2siIGZpbGwtb3BhY2l0eT0iMC4zIi8+Cjwvc3ZnPg==",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

function App() {
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);

  useEffect(() => {
    // Initialize map once
    if (!mapInstanceRef.current) {
      mapInstanceRef.current = L.map(mapContainerRef.current).setView(
        [45.4215, -75.6995],
        10
      );

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 19,
        attribution: "&copy; OpenStreetMap contributors",
      }).addTo(mapInstanceRef.current);
    }

    const map = mapInstanceRef.current;

    const handleDrop = async (e) => {
      e.preventDefault();
      const file = e.dataTransfer.files[0];
      if (!file || !file.name.endsWith(".zip")) {
        alert("Please drop a .zip shapefile");
        return;
      }

      try {
        const arrayBuffer = await file.arrayBuffer();

        // Parse shapefile using shpjs
        const geojson = await shp(arrayBuffer);

        // Add GeoJSON to map
        const layer = L.geoJSON(geojson, {
          onEachFeature: function (feature, layer) {
            if (feature.properties) {
              const content = Object.entries(feature.properties)
                .map(([key, val]) => `<strong>${key}</strong>: ${val}`)
                .join("<br>");
              layer.bindPopup(content);
            }
          },
          style: function (feature) {
            return {
              color: "#3388ff",
              weight: 2,
              opacity: 1,
              fillColor: "#3388ff",
              fillOpacity: 0.2,
              dashArray: null,
            };
          },
          pointToLayer: function (feature, latlng) {
            // For point features, use a circle marker instead of the default icon
            return L.circleMarker(latlng, {
              radius: 8,
              fillColor: "#3388ff",
              color: "#ffffff",
              weight: 2,
              opacity: 1,
              fillOpacity: 0.8,
            });
          },
        }).addTo(map);

        // Fit map to layer bounds
        map.fitBounds(layer.getBounds());
      } catch (error) {
        console.error("Error loading shapefile:", error);
        alert(
          "Error loading shapefile. Please check the file format and ensure it contains .shp, .shx, and .dbf files."
        );
      }
    };

    const handleDragOver = (e) => e.preventDefault();

    window.addEventListener("dragover", handleDragOver);
    window.addEventListener("drop", handleDrop);

    return () => {
      window.removeEventListener("dragover", handleDragOver);
      window.removeEventListener("drop", handleDrop);
    };
  }, []);

  return (
    <>
      <Box
        sx={{
          position: "absolute",
          zIndex: 1000,
          top: 16,
          left: 16,
          background: "rgba(255,255,255,0.9)",
          border: "1px dashed #888",
          px: 2,
          py: 1,
          borderRadius: 1,
          pointerEvents: "none",
        }}
      >
        <Typography variant="body2">Drop .zip shapefile here</Typography>
      </Box>
      <Box
        ref={mapContainerRef}
        sx={{ height: "100vh", width: "100%", position: "relative" }}
      />
    </>
  );
}

export default App;
